// Centralised translations for CATT.
// Keep keys identical across languages — fall back to EN if KO missing.

export const translations = {
  en: {
    nav: {
      research: "Research",
      methods: "Methods",
      projects: "Projects",
      lab: "Lab",
      partners: "Partners",
      publications: "Publications",
      contact: "Contact",
      cta: "Start a Study →",
      status: "LAB ONLINE",
      tagline: "사용성평가연구센터",
    },
    hero: {
      eyebrow: "◢ Usability Evaluation · Human-Centered Research · ISO 9241-210",
      title_a: "We test",
      title_b: "what the",
      title_italic: "future",
      title_accent: "touches.",
      sub: "CATT is an independent usability research lab evaluating the next generation of AI-embedded devices, autonomous robotics, and clinical-grade medical instruments — measuring how humans really use them, before the world does.",
      cta_primary: "Request an Evaluation",
      cta_secondary: "Explore Research",
      study_label: "// Current Study",
      study_name: "Autonomous Surgical Assistant",
      study_meta: "Cohort 18 · Iteration 03 · n=42",
      sus: "SUS",
      task: "Task ✓",
      scroll: "◢ Scroll · Section 01 / 09",
      meta_chips: ["Gaze Analytics", "EEG · GSR", "Task Analysis", "Heuristic Eval", "Co-Design"],
      est: "EST. 2016 · DAEGU · GLOBAL",
    },
    marquee: [
      "Human-AI Interaction", "Surgical Robotics", "Medical UX", "Cognitive Load",
      "Eye-Tracking", "ISO 62366-1", "Heuristic Evaluation", "Voice Interfaces",
      "Autonomous Systems", "Wearables", "EEG · GSR", "Co-Design",
    ],
    about: {
      label: "// 02 — About",
      side_a: "The Center",
      side_b: "CATT / 2016—",
      title_a: "We exist to keep",
      title_b: "advanced technology",
      title_muted: "honest about",
      title_accent: "people.",
      body_a: "CATT — the Center for Advanced Technology and Testing — is a dedicated usability research institute working at the boundary of cognition, engineering, and clinical evidence. We design and run the studies that turn cutting-edge prototypes into products people can actually trust.",
      body_b: "From large language model interfaces to bedside infusion pumps and collaborative robots, our researchers translate behavioural measurement into actionable design guidance — regulated, repeatable, and ruthlessly human-centered.",
      kpis: [
        { k: "10+ yrs", v: "of formative & summative testing" },
        { k: "ISO 62366", v: "medical device usability" },
        { k: "ISO 9241", v: "human-centered design" },
        { k: "n>3,200", v: "study participants to date" },
      ],
    },
    domains: {
      label: "// 03 — Research Domains",
      title_a: "Four edges.",
      title_b: "One method.",
      intro: "Each domain demands its own evidence — surgical safety thresholds differ from chatbot satisfaction. But the underlying science of human performance is one. We bring that science to every brief.",
      cards: [
        {
          title: "AI-Embedded Devices",
          desc: "Voice assistants, AI cameras, smart appliances, and LLM-powered copilots embedded in real products. We test trust calibration, hallucination tolerance, and cognitive offloading.",
          tags: ["Embedded AI", "Trust", "Copilot UX"],
        },
        {
          title: "Robotics",
          desc: "Collaborative arms, surgical assistants, mobile platforms. Measuring shared autonomy, intent legibility, and physical safety.",
          tags: ["Cobots", "Surgical", "Mobile"],
        },
        {
          title: "Medical Devices",
          desc: "Formative and summative testing aligned to IEC 62366-1 and FDA HFE guidance. From infusion pumps to AI diagnostics.",
          tags: ["IEC 62366", "HFE/UE", "FDA"],
        },
        {
          title: "Human-Computer Interaction",
          desc: "Core HCI research: attention, cognitive load, embodied interaction, accessibility. The science under everything else we do.",
          tags: ["Cognition", "A11y", "Embodiment"],
        },
      ],
      domain_word: "Domain",
    },
    methods: {
      label: "// 04 — Methods",
      title_a: "Eight instruments",
      title_b: "for one signal:",
      title_italic: "truth.",
      intro: "We blend behavioural, physiological, and self-report measurement to triangulate what users do, what their bodies say, and what they think they did — because all three usually disagree.",
      items: [
        { name: "Gaze Analytics", desc: "1200Hz gaze, fixation, scanpath, and AOI analytics." },
        { name: "Psychophysiology", desc: "EEG · GSR · ECG · facial EMG synced to interaction." },
        { name: "Task & Error Analysis", desc: "Hierarchical task analysis, use-error scoring, root cause." },
        { name: "Heuristic Evaluation", desc: "Nielsen, Schneiderman, plus domain-tuned heuristics." },
        { name: "Cognitive Walkthrough", desc: "Mental model probing, NASA-TLX, SART, situation awareness." },
        { name: "Summative Validation", desc: "IEC 62366-1 aligned summative HFE studies, 15+ users." },
        { name: "Co-Design Workshops", desc: "Participatory design with clinicians, operators, end-users." },
        { name: "Remote / Diary Studies", desc: "Longitudinal in-context studies with telemetry instrumentation." },
      ],
    },
    stats: {
      studies_completed: "Studies Completed",
      active_partners: "Active Partners",
      publications: "Peer-Reviewed Publications",
      researchers: "Researchers & Clinicians",
    },
    projects: {
      label: "// 06 — Selected Work",
      title_a: "A decade of",
      title_muted: "measurable",
      title_accent: "outcomes.",
      view_case: "View Case Study",
      back: "← All Projects",
      overview: "Overview",
      challenge: "Challenge",
      approach: "Approach",
      findings: "Key Findings",
      methods_used: "Methods Used",
      metric_label: "Headline Result",
      next_project: "Next Project",
      domain_label: "Domain",
      year_label: "Year",
      duration_label: "Duration",
      cohort_label: "Cohort Size",
    },
    lab: {
      label: "// 07 — The Lab",
      title_a: "2,400 m² of",
      title_b: "controlled",
      title_accent: "environment.",
      body: "Our facility houses six instrumented study rooms, a simulated operating theatre, and a remote testing infrastructure that reaches participants on four continents. Calibrated, observed, and recorded down to the millisecond.",
      facilities: [
        "High-fidelity gaze capture suite (1200Hz)",
        "Psychophysiology lab (EEG · GSR · ECG · EMG)",
        "Simulated OR with surgical robot mockups",
        "Clinical room (ISO 14644-1 grade)",
        "Soundproof observation gallery (8-seat)",
        "Remote testing infrastructure (global panel)",
      ],
    },
    partners: {
      label: "// 08 — Partners & Clients",
      title_a: "Trusted by labs, hospitals,",
      title_muted: "and the teams",
      title_accent: "shipping the future.",
    },
    contact: {
      label: "// 09 — Start a Study",
      title_a: "Brief us.",
      title_muted: "We respond in",
      title_accent: "48 hours.",
      body: "Whether you have a prototype, a regulatory deadline, or just a tough human-factors question — share the shape of it. A senior researcher will reply with a scoped path forward.",
      name: "Name",
      email: "Email",
      org: "Organization",
      domain: "Domain",
      message: "Project Brief",
      message_placeholder: "What are you building? Who will use it? What's the riskiest assumption?",
      nda: "NDA available on request.",
      send: "Send Brief",
      sending: "Transmitting…",
      success: "Brief received. A researcher will reach out within 48h.",
      validation: "Please complete name, email and a brief message.",
      error: "Could not submit. Please retry.",
      domains: ["AI-Embedded Devices", "Robotics", "Medical Device", "Other"],
    },
    footer: {
      desc: "Center for Advanced Technology and Testing — an independent usability research lab for AI, robotics, and medical devices.",
      research: "Research",
      lab: "Lab",
      contact: "Contact",
      rights: "All rights reserved.",
      lab_items: ["Methods", "Facilities", "Publications", "Careers"],
    },
    publications: {
      page_label: "// 10 — Publications & Insights",
      title_a: "Peer-reviewed",
      title_muted: "evidence,",
      title_accent: "open access.",
      intro: "Selected publications, conference papers, white papers, and patents from CATT researchers. Filter by year, type, or domain.",
      filter_year: "Year",
      filter_type: "Type",
      filter_domain: "Domain",
      filter_all: "All",
      filter_clear: "Clear filters",
      no_results: "No publications match the current filters.",
      read: "Read paper →",
      total: "publications",
      back_home: "← Back to home",
    },
  },
  ko: {
    nav: {
      research: "연구분야",
      methods: "연구방법",
      projects: "프로젝트",
      lab: "연구실",
      partners: "파트너",
      publications: "논문·인사이트",
      contact: "문의하기",
      cta: "평가 의뢰 →",
      status: "운영 중",
      tagline: "사용성평가연구센터",
    },
    hero: {
      eyebrow: "◢ 사용성 평가 · 인간중심 연구 · ISO 9241-210",
      title_a: "우리는 시험합니다,",
      title_b: "다가올",
      title_italic: "미래가",
      title_accent: "닿을 모든 것을.",
      sub: "CATT는 차세대 AI 시스템, 자율 로보틱스, 임상용 의료기기를 평가하는 독립 사용성 연구소입니다. 세상이 사용하기 전, 사람이 실제로 어떻게 사용하는지 측정합니다.",
      cta_primary: "평가 의뢰하기",
      cta_secondary: "연구분야 보기",
      study_label: "// 진행 중인 연구",
      study_name: "자율 수술 보조 시스템",
      study_meta: "코호트 18 · 반복 03 · n=42",
      sus: "SUS",
      task: "과업 ✓",
      scroll: "◢ 스크롤 · 섹션 01 / 09",
      meta_chips: ["아이트래킹", "EEG · GSR", "과업 분석", "휴리스틱 평가", "공동설계"],
      est: "설립 2014 · 서울 · 글로벌",
    },
    marquee: [
      "휴먼-AI 인터랙션", "수술 로봇", "의료 UX", "인지 부하",
      "아이트래킹", "ISO 62366-1", "휴리스틱 평가", "음성 인터페이스",
      "자율 시스템", "웨어러블", "EEG · GSR", "공동설계",
    ],
    about: {
      label: "// 02 — 소개",
      side_a: "센터 소개",
      side_b: "CATT / 2016—",
      title_a: "첨단 기술이",
      title_b: "사람에게",
      title_muted: "정직하도록,",
      title_accent: "우리는 존재합니다.",
      body_a: "CATT — 사용성평가연구센터(Center for Advanced Technology and Testing)는 인지과학, 공학, 임상 근거의 경계에서 활동하는 전문 사용성 연구기관입니다. 첨단 프로토타입을 신뢰할 수 있는 제품으로 전환시키는 연구를 설계하고 수행합니다.",
      body_b: "거대언어모델 인터페이스부터 병상의 수액 펌프, 협동 로봇에 이르기까지, 우리는 행동 측정을 실행 가능한 디자인 가이드로 번역합니다 — 규제 부합적이고, 재현 가능하며, 철저히 인간 중심으로.",
      kpis: [
        { k: "10년+", v: "형성/총괄 평가 경험" },
        { k: "ISO 62366", v: "의료기기 사용성" },
        { k: "ISO 9241", v: "인간중심 디자인" },
        { k: "n>3,200", v: "누적 연구 참가자" },
      ],
    },
    domains: {
      label: "// 03 — 연구 분야",
      title_a: "네 개의 첨단,",
      title_b: "하나의 방법론.",
      intro: "각 분야는 고유한 근거를 요구합니다 — 수술 안전 기준은 챗봇 만족도와 다릅니다. 그러나 인간 수행에 관한 과학은 하나입니다. 우리는 그 과학을 모든 프로젝트에 적용합니다.",
      cards: [
        {
          title: "AI 시스템",
          desc: "LLM 인터페이스, 자율 에이전트, 멀티모달 코파일럿. 신뢰 보정, 환각 허용도, 인지 분담을 측정합니다.",
          tags: ["LLM UX", "신뢰", "설명가능성"],
        },
        {
          title: "로보틱스",
          desc: "협동 로봇팔, 수술 보조 시스템, 이동 플랫폼. 공유 자율성, 의도 가독성, 물리적 안전성을 평가합니다.",
          tags: ["협동로봇", "수술용", "이동형"],
        },
        {
          title: "의료기기",
          desc: "IEC 62366-1 및 FDA HFE 가이드에 부합하는 형성·총괄 평가. 수액 펌프부터 AI 진단까지.",
          tags: ["IEC 62366", "HFE/UE", "FDA"],
        },
        {
          title: "휴먼-컴퓨터 인터랙션",
          desc: "HCI 기초 연구: 주의, 인지 부하, 체화된 상호작용, 접근성. 모든 작업의 토대가 되는 과학입니다.",
          tags: ["인지", "접근성", "체화"],
        },
      ],
      domain_word: "분야",
    },
    methods: {
      label: "// 04 — 연구 방법",
      title_a: "여덟 가지 도구,",
      title_b: "단 하나의 신호:",
      title_italic: "진실.",
      intro: "행동 측정, 생체신호, 자기보고를 결합해 사용자가 실제로 한 행동, 신체가 말한 반응, 사용자가 생각한 행동 — 세 가지를 삼각측량합니다. 셋은 보통 일치하지 않으니까요.",
      items: [
        { name: "아이트래킹", desc: "120Hz 시선·고정·스캔패스·AOI 분석." },
        { name: "생체신호 계측", desc: "EEG · GSR · ECG · 안면 EMG와 상호작용 동기화." },
        { name: "과업·오류 분석", desc: "계층적 과업 분석, 사용 오류 평가, 근본 원인 분석." },
        { name: "휴리스틱 평가", desc: "닐슨, 슈나이더먼 원칙 + 도메인 특화 휴리스틱." },
        { name: "인지 워크스루", desc: "멘탈 모델 탐색, NASA-TLX, SART, 상황 인식 평가." },
        { name: "총괄 검증", desc: "IEC 62366-1 부합 총괄 HFE 연구, 참가자 15명 이상." },
        { name: "공동설계 워크숍", desc: "임상의·운영자·최종사용자와 함께하는 참여 설계." },
        { name: "원격·다이어리 연구", desc: "텔레메트리 계측이 포함된 종단·맥락 기반 연구." },
      ],
    },
    stats: {
      studies_completed: "수행 연구",
      active_partners: "활동 파트너",
      publications: "동료 심사 논문",
      researchers: "연구원·임상가",
    },
    projects: {
      label: "// 06 — 선별된 작업",
      title_a: "10년간 축적된",
      title_muted: "측정 가능한",
      title_accent: "성과.",
      view_case: "케이스 스터디 보기",
      back: "← 프로젝트 목록",
      overview: "개요",
      challenge: "과제",
      approach: "접근",
      findings: "주요 발견",
      methods_used: "활용된 방법",
      metric_label: "대표 결과",
      next_project: "다음 프로젝트",
      domain_label: "분야",
      year_label: "연도",
      duration_label: "기간",
      cohort_label: "참가자 규모",
    },
    lab: {
      label: "// 07 — 연구실",
      title_a: "2,400㎡ 의",
      title_b: "통제된",
      title_accent: "환경.",
      body: "본 시설은 계측 장비가 갖춰진 6개 연구실, 모의 수술실, 그리고 4개 대륙에 걸친 원격 평가 인프라를 갖추고 있습니다. 보정되고, 관찰되며, 밀리초 단위로 기록됩니다.",
      facilities: [
        "아이트래킹 스위트 (Tobii Pro Spectrum 1200Hz)",
        "생체신호 랩 (BIOPAC EEG · GSR · ECG)",
        "수술 로봇 목업을 갖춘 모의 수술실",
        "임상 공간 (ISO 14644-1 등급)",
        "방음 관찰 갤러리 (8석)",
        "글로벌 원격 평가 인프라",
      ],
    },
    partners: {
      label: "// 08 — 파트너 및 고객",
      title_a: "전 세계 연구실, 병원,",
      title_muted: "그리고 미래를",
      title_accent: "만드는 팀들이 신뢰합니다.",
    },
    contact: {
      label: "// 09 — 연구 의뢰",
      title_a: "프로젝트를 알려주세요.",
      title_muted: "응답까지",
      title_accent: "48시간.",
      body: "프로토타입이든, 인허가 일정이든, 까다로운 인간요인 질문이든 — 개요만 보내주세요. 시니어 연구원이 범위가 정해진 다음 단계를 회신드립니다.",
      name: "성함",
      email: "이메일",
      org: "소속",
      domain: "분야",
      message: "프로젝트 개요",
      message_placeholder: "무엇을 만들고 계신가요? 누가 사용하나요? 가장 위험한 가정은 무엇인가요?",
      nda: "비밀유지계약(NDA) 가능합니다.",
      send: "보내기",
      sending: "전송 중…",
      success: "접수되었습니다. 48시간 이내 연구원이 회신드립니다.",
      validation: "이름, 이메일, 간단한 메시지를 입력해 주세요.",
      error: "전송에 실패했습니다. 다시 시도해 주세요.",
      domains: ["AI 시스템", "로보틱스", "의료기기", "기타"],
    },
    footer: {
      desc: "사용성평가연구센터 — AI, 로보틱스, 의료기기를 위한 독립 사용성 연구소.",
      research: "연구분야",
      lab: "연구실",
      contact: "문의",
      rights: "모든 권리 보유.",
      lab_items: ["연구 방법", "시설", "논문", "채용"],
    },
    publications: {
      page_label: "// 10 — 논문 및 인사이트",
      title_a: "동료 심사를 거친",
      title_muted: "근거,",
      title_accent: "공개 접근.",
      intro: "CATT 연구진의 논문, 학회 발표, 백서, 특허를 모았습니다. 연도, 유형, 분야로 필터링하세요.",
      filter_year: "연도",
      filter_type: "유형",
      filter_domain: "분야",
      filter_all: "전체",
      filter_clear: "필터 초기화",
      no_results: "조건에 맞는 자료가 없습니다.",
      read: "논문 보기 →",
      total: "건",
      back_home: "← 홈으로",
    },
  },
};
